// config/api.ts

/**
 * API configuration for the web client
 * Keeps the same endpoints as the mobile app
 */

// ===== CENTRALIZED API CONFIGURATION =====
// This mirrors the shared-config.js values but is web-compatible
// 🔧 CHANGE PORT HERE - Keep in sync with shared-config.js
export const API_CONFIG = {
  PORT: '5001', // 🔧 CHANGE PORT HERE - Keep in sync with shared-config.js
  LOCALHOST: 'localhost',
  
  // Additional configuration properties needed by apiClient
  TIMEOUT_MS: 10000, // 10 seconds timeout
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
  },
  REFRESH_ENDPOINT: '/auth/refresh',
};

// Helper functions
export const getApiPort = () => API_CONFIG.PORT;

/**
 * Thrown when a built (non-dev, non-test) bundle has no REACT_APP_API_BASE_URL set.
 * We intentionally fail loudly here instead of silently falling back to a default
 * URL: a Vercel deployment (Production or Preview) missing this variable must be
 * visibly broken, not quietly pointed at a dead/wrong backend.
 */
class MissingApiConfigError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'MissingApiConfigError';
  }
}

/** Local dev server and `react-scripts test` (Jest sets NODE_ENV=test) are the only
 *  environments allowed to fall back to localhost instead of a configured var. */
const isLocalOrTestEnv = () =>
  process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test';

/**
 * Returns the base URL for API requests based on the environment
 */
export const getBaseUrl = (): string => {
  // Check for environment variable first (set by Vercel)
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL;
  }

  // In development / test, use localhost with the centralized port
  if (isLocalOrTestEnv()) {
    return `http://${API_CONFIG.LOCALHOST}:${API_CONFIG.PORT}/api`;
  }

  // Any real deployed build (Vercel Production or Preview) must have this set.
  // Refuse to silently serve a default/dead API URL.
  throw new MissingApiConfigError(
    'REACT_APP_API_BASE_URL is not set for this build. Refusing to fall back to a ' +
      "default API URL — set REACT_APP_API_BASE_URL in this Vercel project/environment's " +
      'Environment Variables before deploying.'
  );
};

/**
 * Returns the base URL without /api suffix (for Socket.IO connections)
 */
export const getBaseUrlWithoutApi = (): string => {
  // Check for environment variable first (set by Vercel)
  if (process.env.REACT_APP_SOCKET_URL) {
    return process.env.REACT_APP_SOCKET_URL;
  }

  // If API_BASE_URL is set, remove a trailing /api suffix. Anchored to the end of
  // the string — a plain `.replace('/api', '')` here matched the FIRST occurrence of
  // "/api", which for a host like "https://api.lookbel.com/api" is inside "//api."
  // (the "api" subdomain), silently mangling the result to "https:/.lookbel.com/api"
  // instead of stripping the intended trailing path.
  if (process.env.REACT_APP_API_BASE_URL) {
    return process.env.REACT_APP_API_BASE_URL.replace(/\/api\/?$/, '');
  }

  // In development / test, use localhost with the centralized port (without /api)
  if (isLocalOrTestEnv()) {
    return `http://${API_CONFIG.LOCALHOST}:${API_CONFIG.PORT}`;
  }

  // Any real deployed build (Vercel Production or Preview) must have one of the
  // above set. Refuse to silently serve a default/dead socket URL.
  throw new MissingApiConfigError(
    'Neither REACT_APP_SOCKET_URL nor REACT_APP_API_BASE_URL is set for this build. ' +
      "Refusing to fall back to a default socket URL — set one of them in this Vercel " +
      "project/environment's Environment Variables before deploying."
  );
};

// Also export as default for backward compatibility
export default getBaseUrl;

// Export configuration info for debugging
export const getWebApiConfig = () => ({
  environment: process.env.NODE_ENV,
  baseUrl: getBaseUrl(),
  port: API_CONFIG.PORT,
  localhost: API_CONFIG.LOCALHOST,
  timeout: API_CONFIG.TIMEOUT_MS,
  refreshEndpoint: API_CONFIG.REFRESH_ENDPOINT,
});

// Log configuration on import
console.log('🌐 Web Client API Configuration:', getWebApiConfig());